// ABOUTME: Custom hook providing debounced global search state and query management
// ABOUTME: Returns grouped results from the globalSearch GraphQL query with stale-request protection

import { useCallback, useEffect, useRef, useState } from 'react';

import { useGlobalSearchLazyQuery } from '../graphql/globalSearchQuery.generated';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

export function useGlobalSearch() {
    const [searchTerm, setSearchTerm] = useState('');
    const [open, setOpen] = useState(false);
    const [executeSearch, { data, loading, error }] = useGlobalSearchLazyQuery();
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (searchTerm.length < MIN_SEARCH_LENGTH) {
            setOpen(false);
            return undefined;
        }
        requestIdRef.current += 1;
        const currentRequestId = requestIdRef.current;
        const timeout = setTimeout(() => {
            if (currentRequestId === requestIdRef.current) {
                executeSearch({ variables: { searchTerm } });
                setOpen(true);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timeout);
    }, [searchTerm, executeSearch]);

    const handleSetSearchTerm = useCallback((value: string) => {
        setSearchTerm(value);
        if (value.length < MIN_SEARCH_LENGTH) {
            setOpen(false);
        }
    }, []);

    return {
        searchTerm,
        setSearchTerm: handleSetSearchTerm,
        results: data?.globalSearch?.groups ?? [],
        loading,
        error,
        open,
        setOpen,
    };
}
